import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation, useHistory } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";
import { secureGet, securePost, PostExcel } from "../../config/api";
import { saveAs } from "file-saver";
import { PrimaryButton } from "../../components/PrimaryButton";
import { formatDateTime } from "../../utils/format";
import Table from "../../components/Table";
import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import TableLoadingArea from "../../components/TableLoadingArea";
import SnackbarNotification from "../../components/SnackbarNotification";
import ModuleWrapper from "../../components/ModuleWrapper";

import AddIcon from "../../icons/plusB.svg";
import commentIcon from "../../icons/messagesquare.svg";
import exportIcon from "../../icons/export.svg";

const RepoAdjustments = () => {
  const [adjustmentsData, setAdjustmentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    needle: "",
    dateRange: {},
    repoId: null,
    productId: null,
    userId: null,
  });
  const [repos, setRepos] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(30);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [showModule, setShowModule] = useState(false);

  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const topRef = useRef(null);
  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const showSnackbar = (message, severity = "success", manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const columns = [
    { label: t("columns.number"), key: "number" },
    {
        label: t("columns.createdAt"),
        key: "createdAt",
        render: (date) => formatDateTime(date),
      },
    { label: t("columns.reference"), key: "productReference", dataKey: "product", render: (product) => product?.reference || "N/A" },
    { label: t("columns.name"), key: "productName", dataKey: "product", render: (product) => product?.name || "N/A" },
    { label: t("columns.concernedRepo"), key: "repo", render: (repo) => repo?.name || "N/A" },
    {
        label: t("columns.adjustedBy"),
        key: "user",
        render: (user) => (user ? `${user.firstName} ${user.lastName}` : "N/A"),
      },
    { label: t("columns.quantityBefore"), key: "quantityBefore" },
    { label: t("columns.quantityAfter"), key: "quantityAfter" },
    {
      label: t("columns.variance"),
      key: "gap",
      render: (_, row) => {
        const bg = row.gap > 0 ? "#06D6A0" : row.gap < 0 ? "#EF476F" : "#A8ADC3";
        const label = row.gap > 0 ? `+${row.gap}` : String(row.gap);
        return (
          <div className="flex justify-center">
            <span
              className="inline-flex min-h-[23px] min-w-[28px] items-center justify-center rounded-full px-[4px] py-[2px] text-xs font-semibold text-white tabular-nums"
              style={{ backgroundColor: bg }}
            >
              {label}
            </span>
          </div>
        );
      },
    },
    {
      label: "",
      key: "comment",
      render: (comment) => {
        const text = comment != null ? String(comment).trim() : "";
        if (!text) return null;
        return (
          <div className="flex shrink-0 justify-center" onClick={(e) => e.stopPropagation()}>
            <Tooltip
              title={text}
              placement="top-start"
              arrow
              enterDelay={200}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#231E29",
                    color: "#fff",
                    fontFamily: "Montserrat",
                    fontSize: "12px",
                    fontWeight: "600",
                    padding: "8px 10px",
                    lineHeight: 1.5,
                    maxWidth: 280,
                    whiteSpace: "pre-wrap",
                    "& .MuiTooltip-arrow": { color: "#231E29" },
                  },
                },
              }}
            >
              <span className="relative inline-flex shrink-0 cursor-default rounded-[12px] bg-white px-[6px] py-[4px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                <img
                  src={commentIcon}
                  alt=""
                  className="h-5 w-5 min-h-5 min-w-5 max-h-5 max-w-5 shrink-0"
                />
                <span
                  className="absolute -right-0.5 -top-0.5 block h-2 w-2 rounded-full border border-white bg-[#FEA26F]"
                  aria-hidden
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const filterOptions = [
    {
      name: "needle",
      type: "search",
    },
    {
      name: "dateRange",
      type: "daterange",
    },
    {
      name: "repoId",
      label: t("filters.stock_adjustments.repoId"),
      type: "dropdown",
      options: repos.map((repo) => ({ value: repo.id, label: repo.name })),
    },
    {
      name: "productId",
      label: t("filters.stock_adjustments.productId"),
      type: "dropdown",
      options: products.map((product) => ({
        value: product.id,
        reference: product.reference,
        name: product.name,
      })),
    },
    {
      name: "userId",
      label: t("filters.stock_adjustments.userId"),
      type: "dropdown",
      options: users.map((user) => ({
        value: user.id,
        label: `${user.firstName} ${user.lastName}`,
      })),
    },
  ];

  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [reposRes, productsRes, usersRes] = await Promise.all([
          secureGet("/repos", showSnackbar, { signal }),
          secureGet("/products/unpaginated", showSnackbar, { signal }),
          secureGet("/users?roles=ROLE_ADMIN&roles=ROLE_AGENT", showSnackbar, { signal }),
        ]);
        setRepos(reposRes.data || []);
        setProducts(productsRes.data || []);
        setUsers(usersRes.data || []);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching dropdown options:", error);
        }
      }
    };
    fetchDropdownOptions();
    return () => {
      abortController.abort();
    };
  }, [abortController, signal]);

  const fetchAdjustments = useCallback(
    async (page = 0, size = pageSize, activeFilters = filters) => {
      setLoading(true);
      try {
        const requestBody = {
          page: page,
          size: size,
          repoId: activeFilters.repoId,
          productId: activeFilters.productId,
          userId: activeFilters.userId,
          startDate: activeFilters.startDate,
          endDate: activeFilters.endDate,
          needle: activeFilters.needle,
        };

        console.log(requestBody);

        const { data } = await securePost(`/repoAdjustments`, requestBody, showSnackbar, { signal });
        setAdjustmentsData(data?.content || []);
        setTotalPages(data?.totalPages || 1);
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching stock adjustments:", error);
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, pageSize, signal]
  );

  useEffect(() => {
    if (location.state?.filters || location.state?.page || location.state?.size) {
      const restoredFilters = location.state.filters || {};
      const restoredPage = location.state.page || 0;
      const restoredSize = location.state.size || 30;

      setFilters(restoredFilters);
      setPageNumber(restoredPage);
      setPageSize(restoredSize);

      fetchAdjustments(restoredPage, restoredSize, restoredFilters);
    } else {
      fetchAdjustments(0, pageSize, filters);
    }
  }, []);

  const handleFilterChange = (filterName, filterValue) => {
    const updatedFilters = { ...filters };

    if (filterName === "dateRange") {
      const { startDate, endDate } = filterValue || {};
      updatedFilters.startDate = startDate || "";
      updatedFilters.endDate = endDate || "";
    } else {
      updatedFilters[filterName] = filterValue;
    }

    setFilters(updatedFilters);
    setPageNumber(0);
    fetchAdjustments(0, pageSize, updatedFilters);

    history.replace({
      pathname: location.pathname,
      state: { filters: updatedFilters, page: 0, size: pageSize },
    });
  };

  const resetFilters = () => {
    const defaultFilters = { dateRange: {}, repoId: null, productId: null, userId: null, needle: "" };
    setFilters(defaultFilters);
    setPageNumber(0);
    fetchAdjustments(0, pageSize, defaultFilters);

    history.replace({
      pathname: location.pathname,
      state: { filters: defaultFilters, page: 0, size: pageSize },
    });
  };

  const handleAddClick = () => {
    setShowModule(true);
  };

  const handleExport = async () => {
    const requestBody = {
      repoId: filters.repoId,
      productId: filters.productId,
      userId: filters.userId,
      startDate: filters.startDate || null,
      endDate: filters.endDate || null,
      needle: filters.needle || null,
    };
    try {
      const response = await PostExcel("/repoAdjustments/export", requestBody, showSnackbar);
      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const fileName = `Historique_ajustements_${formattedDate}.xlsx`;
      const blob = new Blob([response.data], {
        type: "application/vnd.ms-excel",
      });
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleModuleClose = useCallback((action) => {
    if (action === "submit") fetchAdjustments();
    setShowModule(false);
  }, [fetchAdjustments]);

  const handlePageChange = (page) => {
    setPageNumber(page);
    fetchAdjustments(page, pageSize, filters);
    if (topRef.current) topRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPageNumber(0);
    fetchAdjustments(0, size, filters);
  };

  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.stock_adjustments")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px]">
        <h1 className="text-[40px] font-semibold">{t("titles.stock_adjustments")}</h1>
        <div className="ml-auto flex items-center gap-2 text-[16px]">
          <PrimaryButton
            label={t("actions.export")}
            icon={exportIcon}
            onClick={() => handleExport()}
          />
          <PrimaryButton
            label={t("primaryButtons.add_stock_adjustment")}
            icon={AddIcon}
            onClick={handleAddClick}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <TableLoadingArea loading={loading}>
        <Table
          columns={columns}
          data={adjustmentsData}
        />
      </TableLoadingArea>

      {showModule && 
        <ModuleWrapper 
          moduleType="repoAdjustments" 
          mode={"add"} 
          onClose={handleModuleClose} 
          onSuccess={(msg) => showSnackbar(msg, 'success')}
        />}

      <PageScroller
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default RepoAdjustments;
